import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CookieCard } from './CookieCard';
import { Cookie } from '../types';
import { fetchProducts } from '../utils/shopify';
const FALLBACK_COOKIES: Cookie[] = [{
  id: 'white-ferrero',
  name: 'WHITE FERRERO',
  badge: 'THE HERO',
  description: 'Classic chocolate chip cookie with walnut chunks, stuffed with a whole Ferrero Rocher and a molten Nutella core. A golden masterpiece.',
  image: 'https://cdn.magicpatterns.com/uploads/t6kfYm4Gxjb4JQCzzP6pnB/banner_pag.png',
  tags: ['nut-lovers', 'chocolate-lovers']
}, {
  id: 'dubai-crunch',
  name: 'DUBAI CRUNCH',
  badge: 'BEST SELLER',
  description: 'Double chocolate decadent base filled with a creamy mix of pistachio cream and toasted kataifi crunch. Pure Middle Eastern luxury.',
  image: 'https://cdn.magicpatterns.com/uploads/qksAy8MYBTMRHTdQzXRPwk/dubai.png',
  tags: ['best-sellers', 'nut-lovers', 'chocolate-lovers']
}, {
  id: 'smores',
  name: "THE S'MORES",
  badge: 'BEST SELLER',
  description: "Toasted marshmallow heart wrapped in golden cookie dough, topped with Hershey's chunks. A campfire masterpiece.",
  image: 'https://cdn.magicpatterns.com/uploads/gMBu7yS1Pkg54qfvjTMErC/smore.png',
  tags: ['best-sellers', 'chocolate-lovers']
}, {
  id: 'churro',
  name: 'CHURRO',
  badge: 'BEST SELLER',
  description: 'Golden cinnamon dough with chocolate chips, stuffed with thick, artisanal Dulce de Leche.',
  image: 'https://cdn.magicpatterns.com/uploads/18dhuazQAfCXw8ZzcYzRCE/heylloyd-image-1774842121303.png',
  tags: ['best-sellers']
}, {
  id: 'double-choc-nutella',
  name: 'DOUBLE CHOCOLATE NUTELLA',
  badge: 'BEST SELLER',
  description: 'Deep dark cocoa dough hugging a legendary molten Nutella center.',
  image: 'https://cdn.magicpatterns.com/uploads/vyEJFLaew7E9wH1R8sry4m/double_nut.png',
  tags: ['best-sellers', 'chocolate-lovers', 'nut-lovers']
}, {
  id: 'midnight-coconut',
  name: 'MIDNIGHT COCONUT',
  description: 'Double chocolate chip cookie stuffed with velvety Raffaello coconut cream.',
  image: 'https://cdn.magicpatterns.com/uploads/tptFmwKnbvEMb6UF1onZbE/midcoco.png',
  tags: ['chocolate-lovers']
}, {
  id: 'birthday-cake',
  name: 'BIRTHDAY CAKE',
  description: 'Classic vanilla sprinkle dough with a gooey marshmallow surprise at the center.',
  image: 'https://cdn.magicpatterns.com/uploads/eSMvhGog5Uq1taXRPCUddU/bcake.png',
  tags: []
}, {
  id: 'crunchy-cookies',
  name: 'CRUNCHY COOKIES: The Golden Bite',
  badge: 'NEW',
  description: 'The ultimate snack for those who crave the perfect sound. These artisanal mini-cookies are baked to golden perfection, achieving an irresistible crunch that shatters into a buttery, rich finish.',
  image: 'https://cdn.magicpatterns.com/uploads/rQsRsFXJCVyqdTpMAwBTTu/crunchy_cookies.png',
  tags: ['chocolate-lovers']
}, {
  id: 'kinderella',
  name: 'KINDERELLA',
  badge: 'NEW',
  description: 'Soft vanilla base with hazelnut chunks and white chocolate chips, exploding with a luxurious white hazelnut cream center.',
  image: 'https://cdn.magicpatterns.com/uploads/poCj9T3BKGnX6Wgyn2P7ys/kinderella.png',
  tags: ['nut-lovers']
}, {
  id: 'kinder-bueno',
  name: 'KINDER BUENO',
  badge: 'NEW',
  description: 'Classic chocolate chip dough with hazelnut chunks, stuffed with white hazelnut cream and an entire Kinder Bueno bar.',
  image: 'https://cdn.magicpatterns.com/uploads/b4gDQ638p8BmYZJyygWoUy/kinder_bueno.png',
  tags: ['nut-lovers', 'chocolate-lovers']
}, {
  id: 'alfajor',
  name: 'ALFAJOR',
  badge: 'NEW',
  description: 'A tropical twist: buttery coconut cookie dough stuffed with a thick, decadent layer of artisanal Dulce de Leche.',
  image: 'https://cdn.magicpatterns.com/uploads/dYwNj77kuALuSjxmwB3sbP/alfajor.png',
  tags: []
}, {
  id: 'pistachio',
  name: 'PISTACHIO',
  badge: 'NEW',
  description: 'Green pistachio-infused dough with white chocolate chunks, stuffed with a velvety, intense pistachio cream heart.',
  image: 'https://cdn.magicpatterns.com/uploads/2psfxyWYnxkVu94BrViiRA/pistachio.png',
  tags: ['nut-lovers']
}];
const filters = [{
  id: 'all',
  label: 'All Flavors'
}, {
  id: 'best-sellers',
  label: 'Best Sellers'
}, {
  id: 'nut-lovers',
  label: 'Nut Lovers'
}, {
  id: 'chocolate-lovers',
  label: 'Chocolate Lovers'
}];
interface ProductGridProps {
  boxItems: Cookie[];
  onAdd: (cookie: Cookie) => void;
  onRemove: (cookieId: string) => void;
}
export function ProductGrid({
  boxItems,
  onAdd,
  onRemove
}: ProductGridProps) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [products, setProducts] = useState<Cookie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    loadProducts();
  }, []);
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const shopifyProducts = await fetchProducts();
      if (shopifyProducts && shopifyProducts.length > 0) {
        setProducts(shopifyProducts);
      } else {
        setProducts(FALLBACK_COOKIES);
      }
    } catch (err) {
      console.error('Failed to load Shopify products, using fallback.', err);
      setError('Failed to load live inventory. Displaying standard menu.');
      setProducts(FALLBACK_COOKIES);
    } finally {
      setLoading(false);
    }
  };
  const filteredCookies = activeFilter === 'all' ? products : products.filter(c => c.tags?.includes(activeFilter));
  const getQuantity = (cookieId: string) => {
    return boxItems.filter(item => item.id === cookieId).length;
  };
  return <section id="menu" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-265">
      <div className="text-center mb-12" data-id="element-266">
        <h2 className="text-4xl md:text-5xl font-bold text-brand-purple mb-4" data-id="element-267">
          THE MENU
        </h2>
        <p className="text-brand-purple/80 text-lg max-w-2xl mx-auto mb-8" data-id="element-268">
          12 dangerously stuffed flavors. Build your 6-Pack or 12-Pack box and
          unlock nationwide free shipping.
        </p>

        {error && <div className="bg-red-100 text-red-700 px-4 py-3 rounded-lg mb-6 inline-flex items-center gap-3 text-sm" data-id="element-269">
            <span data-id="element-270">{error}</span>
            <button onClick={loadProducts} className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase hover:bg-red-700 transition-colors" data-id="element-271">
              Retry
            </button>
          </div>}

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3" data-id="element-272">
          {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-300 ${activeFilter === filter.id ? 'bg-brand-purple text-white shadow-lg scale-105' : 'bg-white/30 text-brand-purple border-2 border-brand-purple/30 hover:bg-brand-purple/10'}`} data-id="element-273">
              {filter.label}
            </button>)}
        </div>
      </div>

      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-274">
          {[...Array(6)].map((_, i) => <div key={i} className="animate-pulse bg-white/20 backdrop-blur-xl border border-white/30 rounded-[24px] p-6 h-[400px]" data-id="element-275">
              <div className="w-full aspect-square rounded-[16px] bg-brand-pink/50 mb-6" data-id="element-276"></div>
              <div className="h-6 bg-brand-purple/20 rounded w-3/4 mb-3" data-id="element-277"></div>
              <div className="h-4 bg-brand-purple/10 rounded w-full mb-2" data-id="element-278"></div>
              <div className="h-4 bg-brand-purple/10 rounded w-5/6 mb-6" data-id="element-279"></div>
              <div className="h-12 bg-brand-purple/20 rounded-full w-full mt-auto" data-id="element-280"></div>
            </div>)}
        </div> : <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-281">
          <AnimatePresence mode="popLayout" data-id="element-282">
            {filteredCookies.map(cookie => <motion.div key={cookie.id} layout initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.3
        }} data-id="element-283">
                <CookieCard cookie={cookie} quantity={getQuantity(cookie.id)} onAdd={onAdd} onRemove={onRemove} data-id="element-284" />
              </motion.div>)}
          </AnimatePresence>
        </motion.div>}
    </section>;
}